import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { Language } from '@core/services';
import { TranslateModule } from '@ngx-translate/core';

/**
 * Modal component for displaying contact information and providing a shortcut to WhatsApp.
 */
@Component({
  selector: 'app-contact-modal',
  imports: [MatDialogModule, TranslateModule],
  templateUrl: './contact-modal.html',
  styleUrl: './contact-modal.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContactModal {
  /**
   * Reference to the dialog instance.
   */
  public readonly dialogRef = inject(MatDialogRef<ContactModal>);

  /**
   * Language service for handling localization.
   */
  private readonly language = inject(Language);

  /**
   * Data injected into the modal, containing the phone number.
   */
  public readonly data: { phone: string; name: string } = inject(MAT_DIALOG_DATA);

  /**
   * Signal holding the phone number to be used in the modal.
   */
  protected readonly phone = signal<string>(this.data.phone);

  /**
   * Opens WhatsApp in a new tab using the provided phone number.
   * Removes spaces, parentheses, plus sign, and other special characters from the phone number.
   */
  protected async goToWhatsApp(): Promise<void> {
    const text = await this.language.getTranslate('geomarker.message_whatsapp', {
      value: this.data.name,
    });

    // Remove spaces, parentheses, plus sign and any other special characters
    const cleanPhone = this.phone().replace(/[\s()+\-]/g, '');
    const whatsappUrl = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(text)}`;

    window.open(whatsappUrl, '_blank');
  }
}
